import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SurveyService from '../../services/survey.service';
import TagService from '../../services/tag.service';
import { ChevronLeft, Plus, X } from 'lucide-react';
import PropTypes from 'prop-types';

const EditQuestion = ({ isNew, questionId }) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [tags, setTags] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'text',
    tag_id: '',
    allows_multiple: false,
    has_text_input: false,
    order: 0,
    options: []
  });

  useEffect(() => {
    loadTags();
    
    // Solo se stiamo modificando e abbiamo un ID valido
    if (!isNew && questionId) {
      console.log("Caricando domanda con ID:", questionId);
      loadQuestion(questionId);
    }
  }, [isNew, questionId]);

  const loadTags = async () => {
    try {
      const tagsData = await TagService.getTags();
      console.log('Tags caricati:', tagsData);
      setTags(tagsData);
      
      // Imposta il tag predefinito se è una nuova domanda
      if (isNew && tagsData.length > 0) {
        setFormData(prev => ({
          ...prev,
          tag_id: tagsData[0].id
        }));
      }
    } catch (error) {
      console.error('Errore durante il caricamento dei tag:', error);
    }
  };

  const loadQuestion = async (id) => {
    try {
      setLoading(true);
      console.log("Facendo chiamata per domanda ID:", id);
      const question = await SurveyService.getQuestionById(id);
      console.log("Domanda caricata:", question);
      
      setFormData({
        title: question.title || '',
        description: question.description || '',
        type: question.type || 'text',
        tag_id: question.tag_id || '',
        allows_multiple: question.allows_multiple || false,
        has_text_input: question.has_text_input || false,
        order: question.order || 0,
        options: question.options || []
      });
      
      setError('');
    } catch (error) {
      console.error('Errore durante il caricamento della domanda:', error);
      setError('Impossibile caricare la domanda');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...formData.options];
    updatedOptions[index].description = value;
    
    setFormData(prev => ({
      ...prev,
      options: updatedOptions
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [
        ...prev.options,
        { id: `temp_${Date.now()}`, description: '' }
      ]
    }));
  };

  const removeOption = (index) => {
    const updatedOptions = [...formData.options];
    updatedOptions.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      options: updatedOptions
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.title.trim() === '') {
      setError('Il titolo è obbligatorio');
      return;
    }
    
    if (formData.type !== 'text' && formData.options.length === 0) {
      setError('Aggiungi almeno un\'opzione per le domande a scelta');
      return;
    }
    
    try {
      // Prepara i dati per l'invio
      const questionData = {
        ...formData,
        // Rimuovi temporanei ID per nuove opzioni
        options: formData.options.map(opt => ({
          description: opt.description,
          ...(opt.id && !opt.id.toString().startsWith('temp_') ? { id: opt.id } : {})
        }))
      };
      
      if (isNew) {
        // Aggiungi una nuova domanda
        await SurveyService.createQuestion(questionData);
      } else {
        // Aggiorna una domanda esistente
        await SurveyService.updateQuestion(questionId, questionData);
      }
      
      navigate('/admin/survey/questions');
    } catch (error) {
      console.error('Errore durante il salvataggio della domanda:', error);
      setError('Errore durante il salvataggio della domanda');
    }
  };

  if (loading) {
    return <div className="p-4">Caricamento...</div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate('/admin/survey/questions')}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-semibold">
          {isNew ? 'Aggiungi Nuova Domanda' : 'Modifica Domanda'}
        </h2>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows={3}
          ></textarea>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
          <input
            type="number"
            name="order"
            value={formData.order}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            min="0"
          />
          <p className="text-xs text-gray-500 mt-1">
              Determina l&apos;ordine di visualizzazione della domanda nel questionario
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
          <select
            name="tag_id"
            value={formData.tag_id}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          >
            <option value="">Seleziona un tag</option>
            {tags.map(tag => (
              <option key={tag.id} value={tag.id}>{tag.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Il tag determina a quali utenti verrà mostrata questa domanda
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo di domanda</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="text">Risposta testuale</option>
            <option value="multiple_choice">Scelta</option>
          </select>
        </div>
        
        {formData.type === 'multiple_choice' && (
          <>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="allows_multiple"
                name="allows_multiple"
                checked={formData.allows_multiple}
                onChange={handleChange}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="allows_multiple" className="ml-2 block text-sm text-gray-700">
                Consenti selezione multipla
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="has_text_input"
                name="has_text_input"
                checked={formData.has_text_input}
                onChange={handleChange}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="has_text_input" className="ml-2 block text-sm text-gray-700">
                Includi campo di testo aggiuntivo
              </label>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Opzioni</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center text-purple-600 hover:text-purple-800"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi opzione
                </button>
              </div>
              
              {formData.options.length === 0 ? (
                  <p className="text-sm text-gray-500">Nessuna opzione. Aggiungi almeno un&apos;opzione.</p>
              ) : (
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={option.id || index} className="flex items-center">
                      <input
                        type="text"
                        value={option.description}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-md"
                        placeholder="Descrizione opzione"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="ml-2 p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-gray-100"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/survey/questions')}
            className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            {isNew ? 'Crea Domanda' : 'Aggiorna Domanda'}
          </button>
        </div>
      </form>
    </div>
  );
};

EditQuestion.propTypes = {
  isNew: PropTypes.bool.isRequired,
  questionId: PropTypes.string
};

export default EditQuestion;